import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { chartColor }                   from '../../shared/components/charts/chart-palette';
import { ChartPointModel }              from '../../shared/components/charts/typings/chart-point';
import { ChartSeriesModel }             from '../../shared/components/charts/typings/chart-series';
import { IntelligenceFormatService }    from '../intelligence.format.service';
import { IntelligenceMechanicJobModel } from '../typings/intelligence.mechanic-job';
import { IntelligenceMechanicModel }    from '../typings/intelligence.mechanic';
import { IntelligenceMechanicsModel }   from '../typings/intelligence.mechanics';
import { IntelligenceService }          from '../intelligence.service';
import { TableSortModel }               from '../../shared/components/charts/typings/table-sort';

@Component({
  selector   : 'wor-intelligence-mechanics-tab',
  template: require('./intelligence.mechanics-tab.component.html')
})
export class MechanicsTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public elrValue          : string = '';
  public filterOptions     : { mechanics : Array<string> } = null;
  public jobs              : Array<IntelligenceMechanicJobModel> = [];
  public jobsLoading       : boolean = false;
  public jobsSort          : TableSortModel = { direction: 'desc', field: 'post_date' };
  public mechanic          : string = '';
  public mechanics         : Array<IntelligenceMechanicModel> = [];
  public performancePoints : Array<ChartPointModel> = [];
  public sort              : TableSortModel = { direction: 'desc', field: 'labour_revenue_ex_tax' };
  public trendPoints       : Array<ChartPointModel> = [];

  public performanceSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') },
    { color: chartColor(0), key: 'cost', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.COST') }
  ];

  public trendSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') }
  ];

  private jobsGeneration : number = 0;
  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    public intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService      : IntelligenceService,
    private translateService        : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  elr ( mechanic : IntelligenceMechanicModel ) : string {
    return mechanic.effective_labour_rate === null || mechanic.effective_labour_rate === undefined
      ? '—'
      : `${ this.intelligenceFormatService.money(mechanic.effective_labour_rate) }/h`;
  }

  jobsTitle () : string {
    const title = this.translateService.instant('INTELLIGENCE_SPACE.MECHANICS.JOBS_TITLE');

    return this.mechanic
      ? `${ title } — ${ this.mechanic }`
      : title;
  }

  jobsCsvRows () : Array<any> {
    return this.jobs.map(job => ({
      post_date      : job.post_date,
      plate_number   : job.plate_number,
      job_card_number: job.job_card_number,
      hours_worked   : job.hours_worked,
      hours_charged  : job.hours_charged,
      amount_charged : job.amount_charged_ex_tax,
      cost           : job.cost_ex_tax,
      gross_profit   : job.gross_profit_ex_tax,
      profit_per_hour: job.profit_per_hour,
      efficiency_pct : job.efficiency_pct
    }));
  }

  profitPerHour ( job : IntelligenceMechanicJobModel ) : string {
    return job.profit_per_hour === null || job.profit_per_hour === undefined
      ? '—'
      : `${ this.intelligenceFormatService.money(job.profit_per_hour) }/h`;
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  clearFilters () : void {
    this.setMechanic('');
  }

  hasActiveFilters () : boolean {
    return !!this.mechanic;
  }

  setMechanic ( mechanic : string ) : void {
    this.mechanic = mechanic;

    this.load();
  }

  toggleMechanic ( mechanic : string ) : void {
    this.setMechanic(this.mechanic === mechanic
      ? ''
      : mechanic);
  }

  sortJobsBy ( field : string ) : void {
    this.jobsSort = {
      direction: this.jobsSort.field === field && this.jobsSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.jobs = this.intelligenceFormatService.sortRows(this.jobs, this.jobsSort.field, this.jobsSort.direction);
  }

  isEmpty () : boolean {
    return !this.mechanics.length;
  }

  isUnderRecovered ( mechanic : IntelligenceMechanicModel ) : boolean {
    return mechanic.recovery_rate !== null
      && mechanic.recovery_rate !== undefined
      && mechanic.recovery_rate < 100;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    ++this.jobsGeneration;

    this.error = false;
    this.jobsLoading = false;
    this.loading = true;

    this.intelligenceService.getMechanics(this.mechanic)
    .then(( data : IntelligenceMechanicsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.mechanics = this.intelligenceFormatService.sortRows(data.mechanics || [], this.sort.field, this.sort.direction);

      // Guarded so a frontend that reaches an environment before the backend does
      // keeps its last known list rather than blanking the dropdown.
      if ( data.filter_options ) {
        this.filterOptions = { mechanics: data.filter_options.mechanics || [] };

        const kept = this.intelligenceFormatService.keepIfOffered(this.mechanic, this.filterOptions.mechanics);

        // The payload was narrowed to a mechanic this period has no option for, so it
        // describes nobody - ask again without them.
        if ( kept !== this.mechanic ) {
          this.mechanic = kept;

          this.load();

          return;
        }
      }

      this.performancePoints = this.mechanics.map(mechanic => ({
        label : mechanic.name,
        values: {
          cost   : mechanic.labour_cost_ex_tax,
          revenue: mechanic.labour_revenue_ex_tax
        }
      }));

      this.trendPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: { revenue: day.labour_revenue_ex_tax }
      }));

      this.elrValue = data.totals && data.totals.effective_labour_rate !== null && data.totals.effective_labour_rate !== undefined
        ? `${ this.intelligenceFormatService.money(data.totals.effective_labour_rate) }/h`
        : '—';

      this.loadJobs();
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  private loadJobs () : void {
    const generation = ++this.jobsGeneration;
    const selected = this.mechanics.find(mechanic => mechanic.name === this.mechanic);

    // The jobs endpoint takes the mechanic as a path segment, so there is nothing to
    // call until one is selected. WS-6694 adds an all-mechanics listing.
    if ( !selected ) {
      this.jobs = [];
      this.jobsLoading = false;

      return;
    }

    this.jobsLoading = true;

    this.intelligenceService.getMechanicJobs(selected.mechanic_id)
    .then(data => {
      if ( generation !== this.jobsGeneration ) {
        return;
      }

      this.jobs = this.intelligenceFormatService.sortRows(data.jobs || [], this.jobsSort.field, this.jobsSort.direction);
    })
    .catch(() => {
      if ( generation === this.jobsGeneration ) {
        this.jobs = [];
      }
    })
    .finally(() => {
      if ( generation === this.jobsGeneration ) {
        this.jobsLoading = false;
      }
    });
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.mechanics = this.intelligenceFormatService.sortRows(this.mechanics, this.sort.field, this.sort.direction);
  }
}
